import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import GoLiveModal from '../components/GoLiveModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const assetUrl = (url) => url ? (url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url) : null;
const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };
const cs = (code) => CURRENCY_SYMBOLS[code] || code + ' ';

const TF_LOGO = 'https://www.targetfunder.com/tf_logo.png';

const MAX_GOALS = 10;
const MAX_POINTS = 40;
const MAX_MINUTE = 70;

// County colours — Kerry green & gold, Mayo green & red.
const TEAMS = {
  kerry: { name: 'Kerry', primary: '#046A38', accent: '#FFB81C', initials: 'CK' },
  mayo: { name: 'Mayo', primary: '#046A38', accent: '#C8102E', initials: 'MO' },
};

// Sample prizes shown in demo mode only — identical content to WC/Golf demos.
const DEMO_PRIZES = [
  { position: 1, title: '1st Place', description: '\u20ac300 Cash Prize', sponsor_name: "Murphy's Bar & Grill", sponsor_logo_url: 'https://static.prod-images.emergentagent.com/jobs/66331e06-c274-4c3d-8473-078ba5e77d7a/images/518c71a17cd11ddccf73e05840d6b8afed5d0d12a5ff3019d8633feb7da15fc2.png' },
  { position: 2, title: '2nd Place', description: '\u20ac200 Voucher', sponsor_name: 'Acme Builders Ltd', sponsor_logo_url: 'https://static.prod-images.emergentagent.com/jobs/66331e06-c274-4c3d-8473-078ba5e77d7a/images/8b1eb4f8c38d5052b459106dd1d04386bae7c74b8a766dcda32e70abdd41d42d.png' },
  { position: 3, title: '3rd Place', description: '\u20ac100 Gift Card', sponsor_name: 'Village Pharmacy', sponsor_logo_url: 'https://static.prod-images.emergentagent.com/jobs/66331e06-c274-4c3d-8473-078ba5e77d7a/images/9db7f47051371bf0c967f92694bee019c8cc4da30e9e93dfdbda4643b888f6c4.png' },
];

function DemoWatermark() {
  return (
    <div className="fixed inset-0 z-[55] pointer-events-none overflow-hidden" aria-hidden="true" data-testid="demo-watermark">
      <div className="absolute inset-0" style={{
        backgroundImage: `repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 80px,
          rgba(29, 53, 87, 0.024) 80px,
          rgba(29, 53, 87, 0.024) 82px
        )`,
      }}>
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute whitespace-nowrap select-none"
            style={{
              top: `${(i * 180) - 100}px`,
              left: '-200px',
              right: '-200px',
              transform: 'rotate(-35deg)',
              transformOrigin: 'center center',
              fontSize: '72px',
              fontWeight: 900,
              fontFamily: 'Barlow Condensed, sans-serif',
              letterSpacing: '0.15em',
              color: 'rgba(29, 53, 87, 0.036)',
              textTransform: 'uppercase',
              lineHeight: 1,
              textAlign: 'center',
            }}
          >
            DEMO &nbsp;&nbsp; DEMO &nbsp;&nbsp; DEMO &nbsp;&nbsp; DEMO &nbsp;&nbsp; DEMO &nbsp;&nbsp; DEMO
          </div>
        ))}
      </div>
    </div>
  );
}

const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(255,255,255,${alpha})`;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return `rgba(255,255,255,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};
const getContrastColor = (hex) => {
  if (!hex) return '#FFFFFF';
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return L > 0.4 ? '#0A0A0A' : '#FFFFFF';
};

// County crests (Wikipedia-hosted official crests, served locally).
const CREST = {
  kerry: '/assets/kerry-crest.png',
  mayo: '/assets/mayo-crest.jpg',
};

// County crest — circular white card with glass ring in the hero, small chip elsewhere.
function CountyCrest({ team, size = 'lg' }) {
  const t = TEAMS[team];
  if (size === 'sm') {
    return (
      <img
        src={CREST[team]}
        alt={`${t.name} crest`}
        className="w-10 h-10 object-contain bg-white rounded-lg p-0.5 ring-1 ring-black/10 flex-shrink-0"
        data-testid={`county-crest-${team}-sm`}
      />
    );
  }
  return (
    <div className="flex flex-col items-center gap-2" data-testid={`county-crest-${team}`}>
      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white p-2.5 shadow-2xl ring-4 ring-white/20 flex items-center justify-center">
        <img src={CREST[team]} alt={`${t.name} crest`} className="w-full h-full object-contain" />
      </div>
      <span
        className="text-xs sm:text-sm font-black uppercase tracking-[0.25em] text-white"
        style={{ fontFamily: 'Barlow Condensed, sans-serif', textShadow: '0 1px 2px rgba(0,0,0,0.35)' }}
      >
        {t.name}
      </span>
    </div>
  );
}

function GaaDemoBanner({ club, onActivate }) {
  return (
    <div className="sticky top-0 z-[60] bg-[#1D3557] text-white px-6 py-5 border-b-2 border-[#E63946]" data-testid="gaa-demo-banner">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="text-[10px] font-bold uppercase tracking-widest bg-[#E63946] px-3 py-3 shrink-0 leading-none">Demo</span>
          <div>
            <p className="text-base sm:text-lg font-bold leading-tight">Like what you see?</p>
            <p className="text-xs sm:text-sm text-white/60">Activate to start raising funds for <strong className="text-white">{club.name}</strong> on All-Ireland Final day</p>
          </div>
        </div>
        <button
          onClick={onActivate}
          className="bg-[#E63946] hover:bg-[#d32f3c] text-white text-sm font-bold uppercase tracking-widest px-8 py-3 transition-colors shrink-0 text-center"
          data-testid="demo-gaa-activate-btn"
        >
          <span>Go Live and Start Fundraising →</span>
        </button>
      </div>
    </div>
  );
}

// Goals/points stepper — big touch targets, clamped.
function ScoreStepper({ label, value, onChange, max, testId }) {
  const clamp = (v) => Math.min(max, Math.max(0, v));
  return (
    <div className="flex-1">
      <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 text-center">{label}</div>
      <div className="flex items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => onChange(clamp(value - 1))}
          className="w-9 h-9 rounded-full border border-gray-300 bg-white text-gray-700 font-bold text-lg leading-none hover:bg-gray-100 active:scale-95 transition-all disabled:opacity-30"
          disabled={value <= 0}
          data-testid={`${testId}-minus`}
          aria-label={`Decrease ${label}`}
        >
          −
        </button>
        <span className="w-12 text-center text-2xl font-black tabular-nums" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid={`${testId}-value`}>{value}</span>
        <button
          type="button"
          onClick={() => onChange(clamp(value + 1))}
          className="w-9 h-9 rounded-full border border-gray-300 bg-white text-gray-700 font-bold text-lg leading-none hover:bg-gray-100 active:scale-95 transition-all disabled:opacity-30"
          disabled={value >= max}
          data-testid={`${testId}-plus`}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
}

export default function GaaFinalPredictorPage({ demoMode = false, demoClub = null }) {
  const { slug } = useParams();
  const [club, setClub] = useState(demoClub);
  const [loading, setLoading] = useState(!demoClub);
  const [notFound, setNotFound] = useState(false);
  const [entriesOpen, setEntriesOpen] = useState(true);

  const [showPicker, setShowPicker] = useState(false);
  const [score, setScore] = useState({ kg: 0, kp: 0, mg: 0, mp: 0 });
  const [minute, setMinute] = useState('');
  const [takenScorelines, setTakenScorelines] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [goLiveOpen, setGoLiveOpen] = useState(false);
  // Winner state: null while loading, false when result not yet published,
  // otherwise an object { result, winner, entries_count }. Drives the
  // post-match "Full-time" banner + winning-entry card at the top of the
  // welcome page. Live pages only (demoMode skips the fetch).
  const [winnerData, setWinnerData] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [clubRes, configRes] = await Promise.all([
          demoClub ? Promise.resolve({ data: demoClub }) : axios.get(`${API}/gaa/clubs/${slug}`),
          axios.get(`${API}/gaa/config`).catch(() => ({ data: { entries_open: true } })),
        ]);
        if (cancelled) return;
        setClub(clubRes.data);
        setEntriesOpen(configRes.data?.entries_open !== false);
        if (!demoClub) {
          axios.get(`${API}/gaa/clubs/${slug}/taken-scorelines`)
            .then((r) => { if (!cancelled) setTakenScorelines(r.data.taken || []); })
            .catch(() => {});
          axios.get(`${API}/gaa/clubs/${slug}/winner`)
            .then((r) => { if (!cancelled && r.data?.published) setWinnerData(r.data); })
            .catch(() => {});
        }
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug, demoClub]);

  useEffect(() => {
    // Set an immediate sport-correct title so the browser tab never shows the
    // static "World Cup 2026" default (from index.html) while the club fetch
    // is still in-flight. Once the club loads we upgrade the title to include
    // the club name.
    const previous = document.title;
    document.title = club && club.name
      ? `${club.name} | All-Ireland Final 2026 Predictor`
      : 'All-Ireland Final 2026 Predictor';
    return () => { document.title = previous; };
  }, [club]);

  const scorelineKey = `${score.kg}-${score.kp}v${score.mg}-${score.mp}`;
  const scorelineTaken = useMemo(
    () => takenScorelines.includes(scorelineKey),
    [takenScorelines, scorelineKey],
  );

  const kTotal = score.kg * 3 + score.kp;
  const mTotal = score.mg * 3 + score.mp;
  const marginText = kTotal === mTotal
    ? 'A draw'
    : kTotal > mTotal
      ? `Kerry by ${kTotal - mTotal}`
      : `Mayo by ${mTotal - kTotal}`;

  const minuteNum = parseInt(minute, 10);
  const minuteValid = Number.isInteger(minuteNum) && minuteNum >= 1 && minuteNum <= MAX_MINUTE;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse text-sm text-gray-400">Loading…</div>
      </div>
    );
  }
  if (notFound || !club) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="text-center max-w-md">
          <p className="text-xs font-bold uppercase tracking-widest text-[#E63946] mb-2">All-Ireland Final 2026 Predictor</p>
          <h1 className="text-2xl font-black text-[#1D3557] mb-2 font-serif-display">Not found</h1>
          <p className="text-sm text-gray-600">This All-Ireland Final predictor hasn&apos;t been set up yet. Visit <a href="/" className="underline">targetfunder.com</a> to learn more.</p>
        </div>
      </div>
    );
  }

  const primary = club.primary_color || '#1D3557';
  const textOnPrimary = getContrastColor(primary);
  const symbol = cs(club.currency || 'EUR');
  const isValid = minuteValid && !scorelineTaken;
  const validityReason = scorelineTaken
    ? 'Scoreline taken'
    : !minuteValid
      ? 'Enter tiebreaker'
      : null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (scorelineTaken) { setError('That exact scoreline is already taken — adjust your score.'); return; }
    if (!minuteValid) { setError(`Tiebreaker required — first-goal minute between 1 and ${MAX_MINUTE}`); return; }
    if (!name.trim() || !email.trim()) { setError('Name and email are required'); return; }
    if (demoMode) { setGoLiveOpen(true); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/gaa/clubs/${slug}/entries`, {
        player_name: name.trim(),
        player_email: email.trim(),
        player_phone: phone.trim() || null,
        kerry_goals: score.kg,
        kerry_points: score.kp,
        mayo_goals: score.mg,
        mayo_points: score.mp,
        first_goal_minute: minuteNum,
      });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      setError('Could not start checkout — please try again');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed — please try again';
      setError(detail);
      if (String(detail).toLowerCase().includes('scoreline')) {
        setTakenScorelines((t) => [...t, scorelineKey]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────
  // PAGE 1 — Welcome
  // ──────────────────────────────────────────────────────────────────────
  if (!showPicker) {
    return (
      <div className="min-h-screen bg-white" data-testid="gaa-welcome">
        {demoMode && <DemoWatermark />}
        {demoMode && <GaaDemoBanner club={club} onActivate={() => setGoLiveOpen(true)} />}
        {demoMode && (
          <GoLiveModal
            open={goLiveOpen}
            onClose={() => setGoLiveOpen(false)}
            club={club}
            demoMode
            sport="gaa"
          />
        )}

        {/* Full-time winner banner — appears once super admin publishes the
            actual result. Sits above the hero band and stays visible when the
            visitor scrolls back to the top. */}
        {winnerData && winnerData.result && (
          <GaaFullTimeBanner
            club={club}
            result={winnerData.result}
            winner={winnerData.winner}
            runnerUp={winnerData.runner_up}
            entriesCount={winnerData.entries_count}
            primary={primary}
            textOnPrimary={textOnPrimary}
          />
        )}

        <section
          className="relative overflow-hidden min-h-[70vh] lg:min-h-[85vh] flex items-center py-16 sm:py-20"
          style={{ backgroundColor: primary, color: textOnPrimary }}
          data-testid="gaa-hero-band"
        >
          {/* Blended Sam Maguire backdrop — luminosity blend + edge mask so the
              photo dissolves into whatever primary colour the club has. */}
          <div className="absolute inset-0 md:left-1/3 z-0 overflow-hidden" data-testid="sam-maguire-image" aria-hidden="true">
            <div
              className="absolute inset-0 bg-cover bg-center md:bg-right opacity-50"
              style={{
                backgroundImage: 'url(/assets/sam-maguire.jpg)',
                mixBlendMode: 'luminosity',
                WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 35%, black 100%)',
                maskImage: 'linear-gradient(to right, transparent 0%, black 35%, black 100%)',
              }}
            />
            <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${primary} 0%, transparent 45%)` }} />
            <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${hexToRgba(primary, 0.6)} 0%, ${hexToRgba(primary, 0.2)} 45%, transparent 75%)` }} />
          </div>

          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10">
            <div className="md:w-3/5">
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] mb-6 sm:mb-8 animate-fade-in-up" style={{ color: hexToRgba(textOnPrimary, 0.7) }}>
                All-Ireland Senior Football Final · Croke Park · Sunday 26 July 2026
              </p>

              <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl font-black leading-[0.92] tracking-tight mb-6 animate-fade-in-up delay-1" data-testid="gaa-hero">
                All-Ireland<br />Final Predictor
              </h1>
              <p className="text-base sm:text-lg max-w-xl leading-relaxed mb-10 animate-fade-in-up delay-2" style={{ color: hexToRgba(textOnPrimary, 0.88) }}>
                Call the exact final score — Kerry v Mayo, goals and points. Nail it and win outright. Bragging rights, prizes, and a club fundraiser rolled into one.
              </p>

              {/* Tale of the tape — Kerry VS Mayo + club chip */}
              <div className="flex flex-wrap items-center gap-6 sm:gap-8 mb-10 animate-fade-in-up delay-2" data-testid="hero-logos">
                <div className="flex items-start gap-3 sm:gap-5">
                  <CountyCrest team="kerry" />
                  <span className="self-center text-xl sm:text-2xl font-black uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: hexToRgba(textOnPrimary, 0.55), letterSpacing: '0.15em' }}>vs</span>
                  <CountyCrest team="mayo" />
                </div>
                <div className="h-14 w-px hidden sm:block" style={{ background: hexToRgba(textOnPrimary, 0.25) }} aria-hidden="true" />
                <div className="flex items-center gap-3">
                  {club.logo_url ? (
                    <img src={assetUrl(club.logo_url)} alt={club.name} className="w-14 h-14 sm:w-16 sm:h-16 object-contain bg-white rounded-full p-1.5 ring-4 ring-white/15 shadow-xl" />
                  ) : (
                    <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center font-black rounded-full text-2xl sm:text-3xl bg-white ring-4 ring-white/15 shadow-xl" style={{ color: primary, fontFamily: 'Playfair Display, Georgia, serif' }}>{club.name.charAt(0)}</div>
                  )}
                  <div className="leading-tight">
                    <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.25em] mb-1" style={{ color: hexToRgba(textOnPrimary, 0.65) }}>A fundraiser for</div>
                    <div className="text-base sm:text-lg font-bold" data-testid="gaa-club-name">{club.name}</div>
                  </div>
                </div>
              </div>

              {(demoMode || entriesOpen) && (
                <div className="animate-fade-in-up delay-3">
                  <button
                    type="button"
                    onClick={() => { setShowPicker(true); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                    className="inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-4 sm:py-5 text-sm sm:text-base font-bold uppercase tracking-[0.2em] rounded-full transition duration-300 hover:-translate-y-1 hover:shadow-2xl shadow-lg"
                    style={{ background: textOnPrimary, color: primary }}
                    data-testid="welcome-pick-cta"
                  >
                    Predict the Score
                    <span aria-hidden="true">→</span>
                  </button>
                  <p className="mt-3 text-xs" style={{ color: hexToRgba(textOnPrimary, 0.7) }}>
                    Entry {symbol}{Number(club.entry_fee || 10).toFixed(0)} · Takes 60 seconds · Entries close at throw-in
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Rules — edge-to-edge grid-borders bento */}
        <section className="px-4 sm:px-6 py-16 sm:py-24 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="mb-10 sm:mb-14 flex items-end justify-between gap-4">
              <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight tracking-tight" style={{ color: primary }}>
                How it works
              </h2>
              <p className="hidden sm:block text-xs uppercase tracking-[0.25em] text-gray-400 font-bold pb-1">Six rules · One winner</p>
            </div>

            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-gray-200 bg-white animate-fade-in-up"
              data-testid="rules-bento"
            >
              {[
                { num: 1, title: 'Predict the score', text: <>Pick the full-time score for <strong>both teams</strong> — goals and points (e.g. Kerry 1-16, Mayo 0-14).</> },
                { num: 2, title: 'Unique scoreline', text: <>Each exact scoreline is taken on a <strong>first-come basis</strong> — no two entries can pick the same score.</> },
                { num: 3, title: 'Exact wins', text: <>Nail the exact score for both teams and you <strong>win outright</strong>.</> },
                { num: 4, title: 'Closest counts', text: <>Nobody exact? Closest on <strong>total points per team</strong> wins. Still level — closest on goals.</> },
                { num: 5, title: 'Tiebreaker', text: <>Still tied? Nearest to the <strong>minute of the first goal</strong> takes it, then earliest entry.</> },
                { num: 6, title: 'Full-time counts', text: <>The result is the final score after normal time — and extra time, if it&apos;s played.</> },
              ].map((rule) => (
                <div
                  key={rule.num}
                  className="relative p-8 lg:p-10 border-r border-b border-gray-200 overflow-hidden group hover:bg-gray-50 transition-colors duration-300"
                  data-testid={`rule-${rule.num}`}
                >
                  <span
                    className="font-serif-display absolute right-4 top-1/2 -translate-y-1/2 text-[84px] font-black select-none pointer-events-none leading-none z-0 group-hover:scale-110 transition-transform duration-500"
                    style={{ color: hexToRgba(primary, 0.14) }}
                    aria-hidden="true"
                  >
                    {rule.num}
                  </span>
                  <div className="relative z-10 pr-14">
                    <div className="text-sm font-bold uppercase tracking-widest mb-2" style={{ color: primary }}>{rule.title}</div>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{rule.text}</p>
                  </div>
                </div>
              ))}

              {/* Match-day panel — full-width band under the six rules */}
              <div
                className="relative p-8 lg:p-10 border-r border-b border-gray-200 flex flex-col justify-center md:col-span-2 lg:col-span-3"
                style={{ background: primary, color: textOnPrimary }}
                data-testid="rules-matchday-cell"
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] mb-2" style={{ color: hexToRgba(textOnPrimary, 0.7) }}>Throw-in</div>
                <div className="font-serif-display text-2xl sm:text-3xl font-black leading-tight">Sunday 26 July</div>
                <div className="text-sm mt-2" style={{ color: hexToRgba(textOnPrimary, 0.85) }}>Croke Park · Entries close at throw-in</div>
              </div>
            </div>
          </div>
        </section>

        {/* Prizes */}
        {(() => {
          const realPrizes = club.prizes || [];
          const prizesToShow = realPrizes.length > 0 ? realPrizes : (demoMode ? DEMO_PRIZES : []);
          if (prizesToShow.length === 0) return null;
          // Single-prize layout: centered, larger card so it doesn't look
          // orphaned in a 3-column grid slot.
          const singular = prizesToShow.length === 1;
          return (
          <section className="px-4 sm:px-6 py-14 bg-gray-50">
            <div className="max-w-5xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-[0.25em] mb-1 text-center" style={{ color: primary }}>{singular ? 'Prize' : 'Prizes'}</p>
              {demoMode && realPrizes.length === 0 && (
                <p className="text-xs text-gray-400 italic mb-4 text-center">Example prizes — you&apos;ll set your own when you activate</p>
              )}
              <div className={singular ? 'flex justify-center mt-4' : 'grid grid-cols-1 sm:grid-cols-3 gap-6 mt-4'} data-testid="prizes-block">
                {prizesToShow.slice(0, 3).map((p, idx) => {
                  const ribbon = ['1st', '2nd', '3rd'][idx];
                  const title = p.title || p.name || ribbon;
                  const sponsor = p.sponsor_name || p.sponsor || '';
                  return (
                    <div
                      key={idx}
                      className={`bg-white border-2 text-center transition-transform duration-300 hover:-translate-y-1 ${singular ? 'p-10 sm:p-14 w-full max-w-xl' : 'p-6 sm:p-8'}`}
                      style={{ borderColor: primary, boxShadow: `8px 8px 0px 0px ${hexToRgba(primary, 0.85)}` }}
                      data-testid={`prize-${idx + 1}`}
                    >
                      <div className={`font-bold uppercase tracking-widest mb-2 ${singular ? 'text-xl sm:text-2xl' : 'text-sm'}`} style={{ color: primary }}>{title}</div>
                      <p className={`text-gray-800 mb-3 ${singular ? 'text-xl sm:text-2xl leading-snug' : 'text-base'}`}>{p.description || p.label || ''}</p>
                      {p.sponsor_logo_url && (
                        <div className="mt-3 pt-4 border-t border-gray-100">
                          <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-2">Sponsored by</p>
                          <img src={assetUrl(p.sponsor_logo_url)} alt={sponsor || 'Sponsor'} className={`mx-auto object-contain mb-2 ${singular ? 'h-28' : 'h-20'}`} />
                          {sponsor && <p className={`font-bold text-gray-700 ${singular ? 'text-base' : 'text-sm'}`}>{sponsor}</p>}
                        </div>
                      )}
                      {sponsor && !p.sponsor_logo_url && (
                        <div className="mt-3 pt-4 border-t border-gray-100">
                          <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Sponsored by</p>
                          <p className={`font-bold text-gray-700 ${singular ? 'text-base' : 'text-sm'}`}>{sponsor}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          );
        })()}

        {/* CTA */}
        <section className="px-6 py-14 bg-gray-50 text-center">
          {(demoMode || entriesOpen) ? (
            <>
              <button
                type="button"
                onClick={() => { setShowPicker(true); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                className="inline-flex items-center justify-center gap-2 px-10 py-5 text-base sm:text-lg font-bold uppercase tracking-[0.2em] rounded-full transition duration-300 hover:-translate-y-1 hover:shadow-2xl shadow-lg"
                style={{ background: primary, color: textOnPrimary }}
                data-testid="welcome-pick-cta-bottom"
              >
                Predict the Score
                <span aria-hidden="true">→</span>
              </button>
              <p className="mt-3 text-xs text-gray-500">Entry {symbol}{Number(club.entry_fee || 10).toFixed(0)} · Takes 60 seconds · Entries close at throw-in</p>
            </>
          ) : (
            <div className="inline-block px-8 py-5 bg-white border border-gray-200 rounded-xl" data-testid="entries-closed-notice">
              <p className="text-base font-black uppercase tracking-widest" style={{ color: primary }}>Entries Closed</p>
              <p className="text-sm text-gray-500 mt-1">The final has thrown in — best of luck to everyone who entered.</p>
            </div>
          )}
        </section>

        <footer className="py-6 px-6 border-t border-gray-200 text-center bg-white" data-testid="gaa-footer">
          <p className="text-gray-500 text-sm">Supporting {club.name} through the All-Ireland Final 2026</p>
          <a
            href="https://www.targetfunder.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 text-xs mt-2 hover:text-gray-600 transition-colors underline"
            data-testid="gaa-terms-link"
          >
            Terms &amp; Conditions
          </a>
          <span className="text-gray-300 text-xs mt-2 mx-2">|</span>
          <a
            href="https://www.targetfunder.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 text-xs mt-2 hover:text-gray-600 transition-colors underline"
            data-testid="gaa-privacy-link"
          >
            Privacy Notice
          </a>

          <p className="mt-4 text-[11px] text-gray-400 max-w-xl mx-auto">
            This predictor is an independent club fundraiser and is not affiliated with, or endorsed by, the GAA.
          </p>

          <div className="mt-5 pt-4 border-t border-gray-200">
            <a
              href="https://www.targetfunder.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-200"
              data-testid="targetfunder-badge"
            >
              <img src={TF_LOGO} alt="TargetFunder" className="h-6 w-auto object-contain" />
              <span className="font-heading font-black text-base uppercase tracking-tighter leading-none text-gray-500">
                Made by <span style={{ color: '#1D3557' }}>target</span><span style={{ color: '#E63946' }}>funder</span><span style={{ color: '#1D3557', opacity: 0.3 }}>.com</span>
              </span>
            </a>
          </div>
        </footer>
      </div>
    );
  }

  // ──────────────────────────────────────────────────────────────────────
  // PAGE 2 — Score picker
  // ──────────────────────────────────────────────────────────────────────
  const scorePanel = (teamKey, g, p, setG, setP) => {
    const t = TEAMS[teamKey];
    const total = g * 3 + p;
    return (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`score-panel-${teamKey}`}>
        <div className="px-4 py-3 flex items-center gap-3 text-white" style={{ background: `linear-gradient(90deg, ${t.primary} 0%, ${t.primary} 70%, ${t.accent} 100%)` }}>
          <img src={CREST[teamKey]} alt={`${t.name} crest`} className="w-8 h-8 object-contain bg-white rounded-full p-0.5 ring-1 ring-black/10 flex-shrink-0" />
          <span className="text-lg font-black uppercase tracking-wider" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{t.name}</span>
          <span className="ml-auto text-2xl font-black tabular-nums" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid={`scoreline-${teamKey}`}>
            {g}-{p}
          </span>
        </div>
        <div className="p-4 flex gap-4">
          <ScoreStepper label="Goals" value={g} onChange={setG} max={MAX_GOALS} testId={`${teamKey}-goals`} />
          <div className="w-px bg-gray-100" aria-hidden="true" />
          <ScoreStepper label="Points" value={p} onChange={setP} max={MAX_POINTS} testId={`${teamKey}-points`} />
        </div>
        <div className="px-4 pb-3 text-center text-xs text-gray-500">= <strong className="text-gray-700">{total}</strong> total points</div>
      </div>
    );
  };

  const predictionSummary = (
    <div className="flex items-center justify-center gap-2 text-sm">
      <span className="font-black tabular-nums" style={{ color: TEAMS.kerry.primary, fontFamily: 'Barlow Condensed, sans-serif', fontSize: '1.15rem' }}>Kerry {score.kg}-{score.kp}</span>
      <span className="text-gray-400">·</span>
      <span className="font-black tabular-nums" style={{ color: TEAMS.mayo.accent, fontFamily: 'Barlow Condensed, sans-serif', fontSize: '1.15rem' }}>Mayo {score.mg}-{score.mp}</span>
      <span className="text-gray-400 text-xs">({marginText})</span>
    </div>
  );

  return (
    <div className="min-h-screen pb-44 lg:pb-32" style={{ background: '#fafafa' }} data-testid="gaa-predictor-page">
      {demoMode && <DemoWatermark />}
      {demoMode && <GaaDemoBanner club={club} onActivate={() => setGoLiveOpen(true)} />}
      {demoMode && (
        <GoLiveModal
          open={goLiveOpen}
          onClose={() => setGoLiveOpen(false)}
          club={club}
          demoMode
          sport="gaa"
        />
      )}

      <header className="bg-white px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 z-40" data-testid="picker-header">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <button onClick={() => { setShowPicker(false); window.scrollTo({ top: 0, behavior: 'instant' }); }} className="text-sm font-bold text-gray-500 hover:text-gray-900 px-2 py-1 -ml-2" data-testid="back-to-welcome">← Back</button>
          {club.logo_url ? (
            <img src={assetUrl(club.logo_url)} alt={club.name} className="w-9 h-9 object-contain" />
          ) : (
            <div className="w-9 h-9 flex items-center justify-center font-bold rounded text-white text-sm" style={{ background: primary }}>{club.name.charAt(0)}</div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold truncate font-serif-display" style={{ color: primary }}>{club.name}</h1>
            <p className="text-[11px] text-gray-500">Kerry v Mayo · Sun 26 July · Croke Park</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* LEFT — score picker */}
          <div className="lg:col-span-2 relative space-y-4" data-testid="picker-and-form-wrap">
            <div className="flex items-baseline justify-between">
              <h2 className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: primary }}>Predict the final score</h2>
              <span className="text-xs text-gray-500">Goals count as 3 points</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {scorePanel('kerry', score.kg, score.kp, (v) => { setError(''); setScore((s) => ({ ...s, kg: v })); }, (v) => { setError(''); setScore((s) => ({ ...s, kp: v })); })}
              {scorePanel('mayo', score.mg, score.mp, (v) => { setError(''); setScore((s) => ({ ...s, mg: v })); }, (v) => { setError(''); setScore((s) => ({ ...s, mp: v })); })}
            </div>

            {/* Live prediction summary */}
            <div className="bg-white border border-gray-200 rounded-xl px-4 py-3" data-testid="prediction-summary">
              {predictionSummary}
              {scorelineTaken && (
                <div className="mt-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs leading-relaxed text-amber-800 text-center" data-testid="scoreline-taken-warning">
                  <strong>That exact scoreline is already taken.</strong> Adjust a goal or a point — each scoreline is first-come, first-served.
                </div>
              )}
            </div>

            {/* Tiebreaker */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5" data-testid="tiebreaker-card">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: primary }}>Tiebreaker</h3>
                <span className="text-[10px] text-gray-400 uppercase tracking-widest">Required</span>
              </div>
              <label className="block text-sm text-gray-700 mb-3">In what minute will the <strong>first goal</strong> of the match be scored?</label>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_MINUTE}
                value={minute}
                onChange={(e) => { setError(''); setMinute(e.target.value); }}
                placeholder="e.g. 16"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-lg font-black text-center tabular-nums"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                data-testid="tiebreaker-minute"
              />
              <span className="ml-3 text-xs text-gray-500">1–{MAX_MINUTE}</span>
              {minute !== '' && !minuteValid && (
                <p className="mt-2 text-xs text-amber-700" data-testid="tiebreaker-invalid">Enter a minute between 1 and {MAX_MINUTE}.</p>
              )}
              <p className="mt-3 text-[11px] text-gray-400 leading-relaxed">
                Match convention applies: a goal scored with the clock reading 15:36 counts as the 16th minute. Closest wins; then earliest entry.
              </p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="entry-error">{error}</div>
            )}

            {/* Mobile-only "Your Details" — the desktop version lives in the
                right sidebar (hidden on mobile via lg:block). Without this,
                mobile entrants had no visible way to enter name/email so the
                sticky bottom Pay button stayed disabled forever. */}
            <div className="lg:hidden bg-white border border-gray-200 rounded-xl p-5" data-testid="your-details-panel-mobile">
              <h3 className="text-base font-black uppercase tracking-widest font-serif-display mb-4" style={{ color: primary }}>Your Details</h3>
              {demoMode ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Name *</label>
                    <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 italic cursor-not-allowed select-none">Entrant puts name here</div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Email *</label>
                    <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 italic cursor-not-allowed select-none">Entrant puts email here</div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Phone (optional)</label>
                    <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 italic cursor-not-allowed select-none">Entrant puts phone here</div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Name *</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" data-testid="entry-name-mobile" required />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Email *</label>
                    <input type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" data-testid="entry-email-mobile" required />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Phone (optional)</label>
                    <input type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base" data-testid="entry-phone-mobile" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-center pt-8 pb-2" data-testid="tf-mid-page">
              <a href="https://www.targetfunder.com" target="_blank" rel="noreferrer noopener">
                <img src={TF_LOGO} alt="Powered by TargetFunder" className="h-5 w-auto grayscale opacity-40 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
              </a>
            </div>
          </div>

          {/* RIGHT — sticky sidebar (desktop) */}
          <aside className="lg:col-span-1 hidden lg:block" data-testid="picker-sidebar">
            <div className="sticky top-24 space-y-4">

              <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="my-prediction-card">
                <h3 className="text-base font-black uppercase tracking-widest font-serif-display mb-4" style={{ color: primary }}>My Prediction</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border-2" style={{ borderColor: TEAMS.kerry.primary, backgroundColor: hexToRgba(TEAMS.kerry.primary, 0.05) }}>
                    <CountyCrest team="kerry" size="sm" />
                    <div className="flex-1 text-sm font-semibold text-gray-900">Kerry</div>
                    <div className="text-xl font-black tabular-nums" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="sidebar-kerry-score">{score.kg}-{score.kp}</div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border-2" style={{ borderColor: TEAMS.mayo.accent, backgroundColor: hexToRgba(TEAMS.mayo.accent, 0.05) }}>
                    <CountyCrest team="mayo" size="sm" />
                    <div className="flex-1 text-sm font-semibold text-gray-900">Mayo</div>
                    <div className="text-xl font-black tabular-nums" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="sidebar-mayo-score">{score.mg}-{score.mp}</div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
                  <span className="text-gray-500 text-xs uppercase tracking-widest font-bold">Result</span>
                  <span className="font-black font-serif-display" style={{ color: primary }} data-testid="sidebar-margin">{marginText}</span>
                </div>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-gray-500 text-xs uppercase tracking-widest font-bold">First goal</span>
                  <span className="font-black font-serif-display" style={{ color: minuteValid ? primary : '#9ca3af' }}>{minuteValid ? `min ${minuteNum}` : '—'}</span>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="your-details-panel">
                <h3 className="text-base font-black uppercase tracking-widest font-serif-display mb-4" style={{ color: primary }}>Your Details</h3>
                {demoMode ? (
                  <div className="space-y-3" data-testid="demo-details-preview">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Name *</label>
                      <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 italic cursor-not-allowed select-none">Entrant puts name here</div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Email *</label>
                      <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 italic cursor-not-allowed select-none">Entrant puts email here</div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Phone (optional)</label>
                      <div className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 italic cursor-not-allowed select-none">Entrant puts phone here</div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Name *</label>
                      <input value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="entry-name" required />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Email *</label>
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="entry-email" required />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-1">Phone (optional)</label>
                      <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="entry-phone" />
                    </div>
                  </div>
                )}
              </div>

              {demoMode ? (
                <div>
                  <button
                    type="button"
                    onClick={(e) => e.preventDefault()}
                    className="w-full px-6 py-3 text-sm font-bold uppercase tracking-widest rounded-lg text-white shadow-md cursor-default"
                    style={{ background: '#FF7F50' }}
                    data-testid="sidebar-demo-pay-btn"
                  >
                    Entrants pay {symbol}{Number(club.entry_fee || 10).toFixed(0)} here
                  </button>
                  <p className="text-[10px] text-gray-400 text-center mt-2">This button goes live when you activate</p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting || !isValid || !name || !email}
                  className="w-full px-6 py-3 text-sm font-bold uppercase tracking-widest rounded-lg disabled:opacity-40 transition-all duration-300 hover:-translate-y-0.5 shadow-md"
                  style={{ background: isValid ? primary : '#9ca3af', color: textOnPrimary }}
                  data-testid="sidebar-pay-btn"
                >
                  {submitting ? 'Loading…' : isValid ? `Pay ${symbol}${Number(club.entry_fee || 10).toFixed(0)} →` : (validityReason || 'Complete your prediction')}
                </button>
              )}
            </div>
          </aside>
        </div>
      </main>

      {/* Mobile-only sticky bottom CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 px-4 sm:px-6 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]" data-testid="sticky-cta">
        {scorelineTaken && (
          <div className="max-w-5xl mx-auto mb-2 px-3 py-1.5 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800 text-center" data-testid="mobile-scoreline-taken-warning">
            <strong>Scoreline taken</strong> — adjust a goal or a point.
          </div>
        )}
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase font-bold tracking-widest text-gray-500">Your prediction</div>
            <div className="text-base font-black font-serif-display truncate" style={{ color: primary }} data-testid="mobile-scoreline-display">
              Kerry {score.kg}-{score.kp} · Mayo {score.mg}-{score.mp}
            </div>
          </div>
          {demoMode ? (
            <div className="flex flex-col items-end">
              <button
                type="button"
                onClick={(e) => e.preventDefault()}
                className="px-6 sm:px-8 py-3 text-sm sm:text-base font-bold uppercase tracking-widest rounded-lg text-white cursor-default"
                style={{ background: '#FF7F50' }}
                data-testid="demo-pay-here-btn"
              >
                Entrants pay {symbol}{Number(club.entry_fee || 10).toFixed(0)} here
              </button>
              <p className="text-[10px] text-gray-400 mt-1 hidden sm:block">This button goes live when you activate</p>
            </div>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !isValid || !name || !email}
              className="px-6 sm:px-8 py-3 text-sm sm:text-base font-bold uppercase tracking-widest rounded-lg disabled:opacity-40 transition-all duration-300 hover:-translate-y-0.5"
              style={{ background: isValid ? primary : '#9ca3af', color: textOnPrimary }}
              data-testid="entry-submit"
            >
              {submitting ? 'Loading…' : isValid ? `Pay ${symbol}${Number(club.entry_fee || 10).toFixed(0)} →` : (validityReason || 'Finish prediction')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * Full-time winner banner shown at the top of the public GAA predictor once
 * the super admin publishes the actual final score. Compact but hero-feeling:
 * final score on the left, winning entrant + their prediction on the right.
 * When nobody entered exactly, we call out the closest pick and show the
 * gap so the winner's write-up (Facebook/WhatsApp post) has everything ready.
 */
function GaaFullTimeBanner({ club, result, winner, runnerUp, entriesCount, primary, textOnPrimary }) {
  const fmt = (g, p) => `${g}-${String(p).padStart(2, '0')}`;
  const resultText = `Kerry ${fmt(result.kerry_goals, result.kerry_points)} v Mayo ${fmt(result.mayo_goals, result.mayo_points)}`;
  const actualKt = result.kerry_goals * 3 + result.kerry_points;
  const actualMt = result.mayo_goals * 3 + result.mayo_points;
  // Winning-margin math for the runner-up line: they finished second on the
  // same points-gap but were further out on goals (rule 4 sub-tiebreaker), or
  // they had a bigger points-gap. Explain briefly on the banner.
  const winnerGap = winner ? (Math.abs(winner.kerry_goals * 3 + winner.kerry_points - actualKt) + Math.abs(winner.mayo_goals * 3 + winner.mayo_points - actualMt)) : null;
  const runnerUpGap = runnerUp ? runnerUp.total_gap : null;
  // Build the runner-up tiebreaker sentence. When both were on the same
  // combined-points gap we lead with the first-goal-minute proximity — it's
  // the most human/relatable stat to name in a social post.
  let runnerUpTiebreak = null;
  if (runnerUp && winner) {
    if (runnerUpGap === winnerGap) {
      const actualMin = result.first_goal_minute;
      const winnerMinOff = (actualMin != null && winner.first_goal_minute != null) ? Math.abs(winner.first_goal_minute - actualMin) : null;
      const runnerMinOff = (actualMin != null && runnerUp.first_goal_minute != null) ? Math.abs(runnerUp.first_goal_minute - actualMin) : null;
      if (winnerMinOff != null && runnerMinOff != null && winnerMinOff !== runnerMinOff) {
        runnerUpTiebreak = `same ${winnerGap}-point gap — ${winner.player_name}'s first-goal-minute prediction was closer (${winnerMinOff} min off vs ${runnerMinOff})`;
      } else {
        // Fall back to the goals sub-tiebreaker when minute data is tied/missing.
        const winnerGoalsOff = Math.abs(winner.kerry_goals - result.kerry_goals) + Math.abs(winner.mayo_goals - result.mayo_goals);
        runnerUpTiebreak = `same ${winnerGap}-point gap — ${winner.player_name} was closer on goals scored (${winnerGoalsOff} off vs ${runnerUp.goals_off})`;
      }
    } else {
      runnerUpTiebreak = `${runnerUpGap}-point gap`;
    }
  }
  return (
    <section
      className="relative overflow-hidden border-b-2"
      style={{ background: primary, color: textOnPrimary, borderColor: hexToRgba(textOnPrimary, 0.15) }}
      data-testid="gaa-fulltime-banner"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 sm:py-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div>
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] mb-2" style={{ color: hexToRgba(textOnPrimary, 0.7) }}>
            Full-time · All-Ireland Final 2026
          </p>
          <p className="font-serif-display font-black leading-none text-4xl sm:text-5xl lg:text-6xl mb-2" data-testid="gaa-fulltime-score">{resultText}</p>
          {result.first_goal_minute != null && (
            <p className="text-sm sm:text-base" style={{ color: hexToRgba(textOnPrimary, 0.75) }}>
              First goal: minute {result.first_goal_minute}
            </p>
          )}
        </div>

        {winner ? (
          <div
            className="rounded-xl p-5 sm:p-6"
            style={{ background: hexToRgba(textOnPrimary, 0.1), border: `1px solid ${hexToRgba(textOnPrimary, 0.2)}` }}
            data-testid="gaa-winner-card"
          >
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] mb-2" style={{ color: hexToRgba(textOnPrimary, 0.7) }}>
              {winner.exact_match ? '🏆 Winner — exact score' : '🏆 Winner — closest score'}
            </p>
            <p className="font-serif-display font-black text-2xl sm:text-3xl leading-tight mb-2" data-testid="gaa-winner-name">
              {winner.player_name}
            </p>
            <p className="text-sm sm:text-base mb-1" data-testid="gaa-winner-pick">
              Predicted <strong>Kerry {fmt(winner.kerry_goals, winner.kerry_points)} v Mayo {fmt(winner.mayo_goals, winner.mayo_points)}</strong>
              {winner.first_goal_minute != null && <> · 1st goal min {winner.first_goal_minute}</>}
            </p>
            {!winner.exact_match && winnerGap != null && (
              <p className="text-xs" style={{ color: hexToRgba(textOnPrimary, 0.7) }}>
                Total-points gap: <strong>{winnerGap}</strong> across both teams — closest of {entriesCount} {entriesCount === 1 ? 'entry' : 'entries'}.
              </p>
            )}
            {runnerUp && !winner.exact_match && (
              <p className="text-xs mt-2 pt-2 border-t" style={{ color: hexToRgba(textOnPrimary, 0.7), borderColor: hexToRgba(textOnPrimary, 0.15) }} data-testid="gaa-runnerup-line">
                Runner-up: <strong>{runnerUp.player_name}</strong> — Kerry {fmt(runnerUp.kerry_goals, runnerUp.kerry_points)} v Mayo {fmt(runnerUp.mayo_goals, runnerUp.mayo_points)}
                {runnerUpTiebreak ? <> ({runnerUpTiebreak}).</> : '.'}
              </p>
            )}
          </div>
        ) : (
          <div
            className="rounded-xl p-5 sm:p-6 text-sm"
            style={{ background: hexToRgba(textOnPrimary, 0.1), border: `1px solid ${hexToRgba(textOnPrimary, 0.2)}` }}
            data-testid="gaa-no-winner"
          >
            No paid entries for {club?.name || 'this club'} — no winner to declare.
          </div>
        )}
      </div>
    </section>
  );
}
